
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import PaginationBar from './components/PaginationBar';
import ResponsiveShadow from './components/ResponsiveShadow';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import SreaModal from './components/modals/SreaModal';
import usePagination10 from './components/hooks/usePagination10';
import useReferenceData from './components/hooks/useReferenceData';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import { formatCurrency } from './utils/formatMoeda';
import Toolbar from './components/Toolbar';
import LoadingModal from './components/modals/LoadingModal';


const emptyForm = { idPrograma: '', idConta: '', vlrC: '', vlrK: '' };

function Srea() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const ano = localStorage.getItem('ano');
  const isAdmin = JSON.parse(localStorage.getItem('vinculo') || '{}')?.tipo === 'ADMIN';
  const isMaster = JSON.parse(localStorage.getItem('user') || '{}')?.role === 'MASTER';
  const canImport = isAdmin || isMaster;
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ ...emptyForm });
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { setPagination } = usePagination10(items.length);
  const { programas, contas } = useReferenceData(token, cnpj);

  // Ordena os itens por `idConta` antes de aplicar filtros/paginação
  const sortedItems = [...items].sort((a, b) => {
    const aConta = String(a.idConta || '');
    const bConta = String(b.idConta || '');
    return aConta.localeCompare(bConta, undefined, { numeric: true, sensitivity: 'base' });
  });

  const displayed = sortedItems.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  const resumoPorPrograma = useMemo(() => {
    const map = {};
    (Array.isArray(items) ? items : []).forEach(it => {
      const chave = String(it.idPrograma);
      if (!map[chave]) map[chave] = { idPrograma: it.idPrograma, custeio: 0, capital: 0, qtd: 0 };
      map[chave].custeio += parseFloat(it.vlrC || 0);
      map[chave].capital += parseFloat(it.vlrK || 0);
      map[chave].qtd += 1;
    });
    return Object.values(map);
  }, [items]);

  useEffect(() => {
    if (!cnpj || !ano) { showDefaultModal('error', 'Escola ou ano não selecionado.'); setIsLoading(false); return; }
    setIsLoading(true);
    axios.get(`${API_BASE_URL.SREA}/search`, {
      params: { idEscola: cnpj, ano }, headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => {
        setItems(r.data);
      })
      .catch(err => {
        // Only show modal if error is not just empty data
        if (err.response && err.response.status !== 200 && err.response.status !== 404) {
          showDefaultModal('error', 'Erro ao carregar SREA.');
        }
      })
      .finally(() => setIsLoading(false));
  }, [token, cnpj, ano, atualizar]);

  function handleChange(e) { const { name, value } = e.target; setForm(f => ({ ...f, [name]: value })); }
  function handleNew() { setForm({ ...emptyForm, _isEditing: false }); setShowModal(true); }
  function handleEdit() { if (selected.length !== 1) return; setForm({ ...selected[0], _isEditing: true }); setShowModal(true); }

  function handleSave(e) {
    e.preventDefault();
    axios.post(`${API_BASE_URL.SREA}/save`, { ...form, idEscola: cnpj, ano }, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        setShowModal(false);
        setAtualizar(a => !a);
        setSelected([]);
      })
      .catch(err => {
        let msg = 'Erro ao salvar SREA.';
        if (err.response && err.response.data && err.response.data.message) {
          msg += '\n' + err.response.data.message;
        }
        showDefaultModal('error', msg);
      });
  }

  function handleDelete() {
    if (selected.length === 0) return;
    showConfirmModal('delete', () => Promise.all(selected.map(s =>
      axios.delete(`${API_BASE_URL.SREA}/${ano}/${s.idPrograma}/${cnpj}/${s.idConta}`, { headers: { Authorization: `Bearer ${token}` } })
    ))
      .then(() => { setSelected([]); setAtualizar(a => !a); })
      .catch(() => showDefaultModal('error', 'Erro ao excluir SREA.'))
    );
  }

  function handleImportarSresAnterior() {
    if (!cnpj || !ano) { showDefaultModal('error', 'Escola ou ano não selecionado.'); return; }
    const anoInt = parseInt(ano, 10);
    if (Number.isNaN(anoInt)) { showDefaultModal('error', 'Ano inválido.'); return; }
    const anoAnterior = anoInt - 1;

    axios.get(`${API_BASE_URL.SREA}/existe-dados`, {
      params: { idEscola: cnpj, ano }, headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => {
        const jaExiste = !!r.data;
        const preambulo = `Você está no ano **${anoInt}**.\n` +
          `O saldo será importado do **Saldo Reprogramado Exercício Seguinte (SRES)** do ano anterior (**${anoAnterior}**).`;
        const avisoExistente = jaExiste
          ? `\n\n**ATENÇÃO:** já existem informações de SREA cadastradas para o ano ${anoInt}. Ao importar, elas serão **substituídas** pelos dados do SRES do ano ${anoAnterior}.`
          : `\n\nNão existem informações de SREA para o ano ${anoInt} até o momento.`;
        showConfirmModal('importSres', () => executarImportacao(anoInt), preambulo + avisoExistente);
      })
      .catch(() => showDefaultModal('error', 'Erro ao verificar informações existentes de SREA.'));
  }

  function executarImportacao(anoInt) {
    setIsLoading(true);
    axios.post(`${API_BASE_URL.SREA}/importar-sres`, null, {
      params: { idEscola: cnpj, ano: anoInt }, headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => {
        setSelected([]);
        setAtualizar(a => !a);
        const quant = r.data?.importados ?? 0;
        showDefaultModal('success', `Saldos importados com sucesso!\n${quant} registro(s) do SRES ${anoInt - 1} foram importados para o SREA ${anoInt}.`);
      })
      .catch(err => {
        let msg = 'Erro ao importar saldos.';
        if (err.response && err.response.data && err.response.data.message) {
          msg = err.response.data.message;
        }
        showDefaultModal('error', msg);
      })
      .finally(() => setIsLoading(false));
  }

  function keyOf(item) { return `${ano}_${item.idPrograma}_${cnpj}_${item.idConta}`; }
  function toggleSelect(item) {
    setSelected([item]);
  }

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="Saldo Reprogramado Exercício Anterior" icon="bi-cash" />
        <PageContent>
          <Toolbar
            className="mb-3"
            onNew={isAdmin ? handleNew : undefined}
            onEdit={isAdmin ? handleEdit : undefined} disableEdit={selected.length !== 1}
            onDelete={isAdmin ? handleDelete : undefined} disableDelete={selected.length === 0}
          >
            {canImport && (
              <button type="button" className="btn btn-primary btn-sm botao-igual" onClick={handleImportarSresAnterior}>
                <i className="bi bi-download me-1"></i>Importar Saldo do Exercício Anterior
              </button>
            )}
          </Toolbar>
          {resumoPorPrograma.length > 0 && (
            <div className="mb-2 d-flex flex-wrap gap-2" style={{ maxHeight: '30vh', overflowY: 'auto', overflowX: 'hidden', flexShrink: 0 }}>
              {resumoPorPrograma.map(prog => (
                <div className="card border-0 shadow-sm" key={String(prog.idPrograma)} style={{ borderRadius: 10, flex: '1 1 0', minWidth: 170, maxWidth: '25%' }}>
                  <div className="card-body pt-2 pb-1 px-3">
                    <div className="d-flex justify-content-between align-items-center gap-2" style={{ backgroundColor: '#198754', color: '#fff', margin: '-0.5rem -1rem 0.5rem', padding: '0.15rem 1rem', borderRadius: '10px 10px 0 0', minHeight: '22px' }}>
                      <span className="fw-bold text-truncate" style={{ fontSize: '.62rem' }} title={programas.find(p => p.id === prog.idPrograma)?.descricao || prog.idPrograma}>{programas.find(p => p.id === prog.idPrograma)?.descricao || prog.idPrograma}</span>
                      <span className="badge text-bg-light text-success flex-shrink-0" style={{ fontSize: '.62rem' }}>{prog.qtd}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">Custeio</span><span>{formatCurrency(prog.custeio)}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">Capital</span><span>{formatCurrency(prog.capital)}</span>
                    </div>
                    <div className="d-flex justify-content-between pt-1 mt-1 border-top fw-bold" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span>Total</span><span className="text-success">{formatCurrency(prog.custeio + prog.capital)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <TableContainer isLoading={false}>
              <DataTable head={
                  <tr>
                    <th style={{ width: '3rem' }}>#</th>
                    <th>Programa</th>
                    <th>Conta</th>
                    <th className="text-end">Valor Custeio</th>
                    <th className="text-end">Valor Capital</th>
                    <th className="text-end">Valor Total</th>
                  </tr>
                }>
                <tbody>
                  {displayed.map((item, idx) => (
                    <tr key={keyOf(item)} className={selected.find(s => keyOf(s) === keyOf(item)) ? 'table-success' : ''} onClick={() => toggleSelect(item)} style={{ cursor: 'pointer' }}>
                      <td>{setPagination.startIndex + idx + 1}</td>
                      <td>{programas.find(p => p.id === item.idPrograma)?.descricao || item.idPrograma}</td>
                      <td>{item.idConta}</td>
                      <td className="align-middle text-end">{formatCurrency(item.vlrC)}</td>
                      <td className="align-middle text-end">{formatCurrency(item.vlrK)}</td>
                      <td className="align-middle text-end fw-bold text-success" style={{ fontSize: '0.75rem' }}>{formatCurrency(parseFloat(item.vlrC || 0) + parseFloat(item.vlrK || 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
          </TableContainer>
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>
      <SreaModal show={showModal} onClose={() => setShowModal(false)} onSubmit={handleSave} form={form} handleChange={handleChange} programas={programas} />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}

export default Srea;
